import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, Building2, Lock } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Money } from "./SaudiRiyal";
import { api } from "../lib/api";

const newId = () => Math.random().toString(36).slice(2, 10);
const monthly = (i) => (i.period === "yearly" ? (Number(i.amount) || 0) / 12 : Number(i.amount) || 0);

export const FixedCostsCard = ({ canEdit }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [auto, setAuto] = useState(null);
  useEffect(() => { api.getFixedCosts().then((r) => { setItems(r.items); setAuto(r.auto); }).catch(() => {}); }, []);
  const set = (id, k, v) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const save = async () => {
    setBusy(true);
    try { const r = await api.saveFixedCosts(items.map((x) => ({ ...x, amount: Number(x.amount) || 0 }))); setItems(r.items); toast.success("تم حفظ قالب المصروفات الثابتة"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  const total = items.reduce((s, i) => s + monthly(i), 0);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="fixed-costs-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div><h3 className="font-heading font-bold text-slate-900 flex items-center gap-1.5"><Lock className="h-4 w-4 text-slate-400" /> المصروفات الثابتة</h3>
          <p className="text-xs text-slate-500">تُعبّأ مرة واحدة وتتكرر كل شهر، وتُقسم بالتساوي على المناديب المقيمين فقط</p></div>
        <div className="text-end"><p className="text-[11px] text-slate-500">البنود اليدوية شهرياً</p><p className="font-heading text-xl font-bold text-red-600" data-testid="fixed-total"><Money value={Math.round(total * 100) / 100} /></p></div>
      </div>
      {auto && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3 text-sm" data-testid="fixed-auto">
          <p className="flex items-center gap-1.5 font-semibold text-indigo-800"><Building2 className="h-4 w-4" /> تلقائي من سجلات الشركة (المصاريف الثابتة)</p>
          <p className="mt-1 text-xs text-slate-600">الإجمالي السنوي <b><Money value={auto.total} /></b> ÷ <b>{auto.residents}</b> مقيم = <b className="text-indigo-700" data-testid="fixed-auto-month"><Money value={auto.per_rider_month} /></b> شهرياً لكل مندوب — يُضاف تلقائياً في الربحية ويتغيّر مع دخول وخروج المقيمين.</p>
        </div>
      )}
      <p className="text-xs font-semibold text-slate-600">بنود إضافية يدوية (غير موجودة في سجلات الشركة):</p>
      <div className="space-y-2">
        {items.map((i) => (
          <div key={i.id} className="grid grid-cols-12 gap-2 items-center" data-testid={`fixed-row-${i.id}`}>
            <Input className="col-span-12 sm:col-span-5" disabled={!canEdit} value={i.label} placeholder="اسم البند (مثال: اشتراك قوى)" onChange={(e) => set(i.id, "label", e.target.value)} data-testid={`fixed-label-${i.id}`} />
            <Input className="col-span-5 sm:col-span-3" type="number" disabled={!canEdit} value={i.amount} onChange={(e) => set(i.id, "amount", e.target.value)} data-testid={`fixed-amount-${i.id}`} />
            <select className="col-span-4 sm:col-span-2 h-9 rounded-lg border border-slate-200 bg-white px-1.5 text-xs" disabled={!canEdit} value={i.period} onChange={(e) => set(i.id, "period", e.target.value)} data-testid={`fixed-period-${i.id}`}>
              <option value="monthly">شهري</option><option value="yearly">سنوي (÷12)</option>
            </select>
            <span className="col-span-2 sm:col-span-1 text-[11px] text-slate-500 text-center"><Money value={Math.round(monthly(i))} /></span>
            {canEdit && <button className="col-span-1 text-red-500 hover:text-red-700 justify-self-center" onClick={() => setItems((xs) => xs.filter((x) => x.id !== i.id))} data-testid={`fixed-del-${i.id}`}><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
        {!items.length && <p className="text-center text-xs text-slate-400 py-3">لا توجد بنود بعد</p>}
      </div>
      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="gap-1" onClick={() => setItems((xs) => [...xs, { id: newId(), label: "", amount: 0, period: "monthly" }])} data-testid="fixed-add"><Plus className="h-4 w-4" /> أضف بنداً</Button>
          <Button size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={save} data-testid="fixed-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ القالب</Button>
        </div>
      )}
    </div>
  );
};
